// Lote A: las inspecciones FIRMADAS de Inspecciones v2 calculan el estado del Checklist de los ítems que sus preguntas
// evidencian (columna anexo_item de cada pregunta, con el id interno del Checklist; la pantalla muestra el número legal).
// Ítems: SP 1 a SP 9 (SP-01 a SP-09), GT 9 (GT-14) y los nuevos GT 14, 21, 22, 26, 30 y 35 (GT-20 a GT-25).
// Etapa 5a (M60): GT 13, 18, 19, 20, 23, 24 y 25 (GT-40 a GT-46) con el mismo cálculo; SP 9 con una pregunta por
// condición del Anexo 1 (cumple solo si todas cumplen: cualquier falla cuenta como en los demás ítems).
// Reglas DECIDIDAS POR CLAUDE, por validar con Kevin:
//  - Cuenta la última inspección firmada con preguntas del ítem que esté dentro de su vigencia: la frecuencia de su
//    programación activa más 15 días de gracia; sin programación (o de frecuencia única), 12 meses.
//  - Cumple: todas las respuestas del ítem en Cumple, o en No cumple con su hallazgo cerrado (cerrar exige evidencia, M41).
//  - No cumple: alguna falla con el hallazgo vencido, o sin hallazgo.
//  - En proceso: alguna falla con el hallazgo abierto dentro de su plazo.
//  - Pendiente: ninguna inspección firmada vigente.
//  - Todas las respuestas del ítem en "No aplica": el cálculo SUGIERE No aplica, pero no lo escribe (M15: solo el
//    administrador, con justificación); el botón "Usar cálculo automático" lo aplica con la justificación del cálculo.
//  - Lo marcado a mano nunca se sobrescribe: se muestra "el cálculo automático dice: X" con "Usar cálculo automático".
//  - GT 9: si hay una inspección v2 firmada vigente con preguntas de GT 9, manda v2; si no, sigue el módulo anterior
//    (inspection-compliance-sync.ts) y la traza lo dice.
//  - GT 22: los luxes medidos se comparan con el mínimo para circulación y pasillos del módulo de Iluminación.

import { supabase } from '../../supabaseClient';
import type { Traza } from '../pe/pe-tipos';
import { esManual, type EstadoGuardadoChecklist, type EstadoItem, type TrazaItemPlan } from '../plan-emergencias-compliance-sync';
import { EM_TABLE_ART10 } from '../iluminacion';
import { plural } from '../plurales';
import { esFalla, estadoHallazgo, fechaEc, isoLocal, itemsDe, sumarMeses } from './insp-logica';
import type { FilaHallazgo, FilaInspeccion, FilaProgramacion, FilaRespuesta, FotoPlantilla, Frecuencia, ItemFoto } from './insp-tipos';

export const FUENTE_INSPECCIONES_ANEXO1 = 'inspecciones_v2_anexo1';
export const MODULO_INSPECCIONES_ANEXO1 = 'Inspecciones (motor nuevo)';
export const MODULO_INSPECCIONES_ANTERIOR = 'Inspecciones (módulo anterior)';
export const NORMA_REGLA_ANEXO1 = 'Criterio técnico de StorSafe (por validar)';
export const FUENTE_ANEXO1 = 'Anexo 1, Lista de verificación de cumplimiento de obligaciones de seguridad y salud en el trabajo (Ministerio del Trabajo)';

export const ITEMS_SP_INSPECCION = ['SP-01', 'SP-02', 'SP-03', 'SP-04', 'SP-05', 'SP-06', 'SP-07', 'SP-08', 'SP-09'] as const;
export const ITEM_GT9 = 'GT-14';
export const ITEMS_GT_NUEVOS = ['GT-20', 'GT-21', 'GT-22', 'GT-23', 'GT-24', 'GT-25'] as const;
// Mantenimiento M1 (M50): GT 10, 11, 27 y 28 desde las inspecciones por activo firmadas (mismo cálculo).
export const ITEMS_GT_MANTENIMIENTO = ['GT-26', 'GT-27', 'GT-28', 'GT-29'] as const;
// Etapa 5a (M60): condiciones e higiene. Ids nuevos (GT-18 y GT-19 los usaron PDFs antiguos; GT-20 a GT-36 están ocupados).
export const ITEMS_GT_CONDICIONES_HIGIENE = ['GT-40', 'GT-41', 'GT-42', 'GT-43', 'GT-44', 'GT-45', 'GT-46'] as const;
export const ITEMS_ANEXO1_INSPECCIONES: string[] = [...ITEMS_SP_INSPECCION, ITEM_GT9, ...ITEMS_GT_NUEVOS, ...ITEMS_GT_MANTENIMIENTO,
  ...ITEMS_GT_CONDICIONES_HIGIENE];

/** Número legal del Anexo 1 de cada ítem (para los textos). */
export const LEGAL_ANEXO1: Record<string, string> = {
  'GT-30': 'GT 17', 'GT-31': 'GT 31', 'GT-32': 'GT 32', 'GT-33': 'GT 33', 'GT-34': 'GT 34', 'GT-36': 'GT 36',   // M51 (insp-lote-g.ts)

  'SP-01': 'SP 1', 'SP-02': 'SP 2', 'SP-03': 'SP 3', 'SP-04': 'SP 4', 'SP-05': 'SP 5', 'SP-06': 'SP 6', 'SP-07': 'SP 7',
  'SP-08': 'SP 8', 'SP-09': 'SP 9', 'GT-14': 'GT 9', 'GT-20': 'GT 14', 'GT-21': 'GT 21', 'GT-22': 'GT 22', 'GT-23': 'GT 26',
  'GT-24': 'GT 30', 'GT-25': 'GT 35', 'GT-26': 'GT 10', 'GT-27': 'GT 11', 'GT-28': 'GT 27', 'GT-29': 'GT 28',
  'GT-40': 'GT 13', 'GT-41': 'GT 18', 'GT-42': 'GT 19', 'GT-43': 'GT 20', 'GT-44': 'GT 23', 'GT-45': 'GT 24', 'GT-46': 'GT 25',   // M60
};
const legal = (item: string) => LEGAL_ANEXO1[item] ?? item;

export const DIAS_GRACIA = 15;
export const MESES_SIN_PROGRAMACION = 12;
export const ITEM_LUX = 'GT-22';
/** Mínimo para áreas de circulación y pasillos: el mismo valor y la misma cita que usa el módulo de Iluminación. */
export const MIN_LUX_CIRCULACION = EM_TABLE_ART10.circulacion_pasillos.min;
export const CITA_LUX_CIRCULACION = 'Art. 10.4 del Anexo 3 AM 196';
const NORMA_LUX = 'AM 196 Anexo 3';
const lux = (n: number) => String(n).replace('.', ',');

export type EstadoCalculoAnexo1 = EstadoItem | 'no_aplica';

export type InspAnexo1 = Pick<FilaInspeccion, 'id' | 'status' | 'signed_at' | 'performed_date' | 'schedule_id' | 'template_id'>
  & { template_snapshot: Pick<FotoPlantilla, 'sections'> | null };
export type RespAnexo1 = Pick<FilaRespuesta, 'inspection_id' | 'item_key' | 'value' | 'number_value'>;
export type HallAnexo1 = Pick<FilaHallazgo, 'inspection_id' | 'item_key' | 'status' | 'due_date'>;
export type ProgAnexo1 = Pick<FilaProgramacion, 'id' | 'template_id' | 'frequency' | 'active' | 'end_date'>;

// ── Vigencia ──
const MESES_FRECUENCIA: Record<Frecuencia, number | null> = { unica: null, semanal: 0, mensual: 1, trimestral: 3, semestral: 6, anual: 12 };
const ETIQUETA_PERIODO: Record<Frecuencia, string> = {
  unica: '12 meses', semanal: '1 semana', mensual: '1 mes', trimestral: '3 meses', semestral: '6 meses', anual: '12 meses',
};

export function restarDias(iso: string, dias: number): string {
  const [a, m, d] = iso.split('-').map(Number);
  const f = new Date(Date.UTC(a, m - 1, d - dias));
  return `${f.getUTCFullYear()}-${String(f.getUTCMonth() + 1).padStart(2, '0')}-${String(f.getUTCDate()).padStart(2, '0')}`;
}

/** Programación activa de la inspección: la suya si sigue activa; si no, una activa de la misma plantilla. */
export function programacionDe(i: Pick<InspAnexo1, 'schedule_id' | 'template_id'>, progs: ProgAnexo1[], hoyIso: string): ProgAnexo1 | null {
  const activas = progs.filter(p => p.active && (!p.end_date || p.end_date >= hoyIso) && p.frequency !== 'unica');
  return activas.find(p => p.id === i.schedule_id) ?? activas.find(p => !!i.template_id && p.template_id === i.template_id) ?? null;
}

/** Desde qué fecha (inclusive) una inspección realizada sigue vigente hoy, y el texto de la ventana. */
export function inicioVigencia(frecuencia: Frecuencia | null, hoyIso: string): { desde: string; ventana: string } {
  const meses = frecuencia ? MESES_FRECUENCIA[frecuencia] : null;
  if (meses === null) return { desde: sumarMeses(hoyIso, -MESES_SIN_PROGRAMACION), ventana: `${MESES_SIN_PROGRAMACION} meses (sin programación)` };
  const base = meses === 0 ? restarDias(hoyIso, 7) : sumarMeses(hoyIso, -meses);
  return { desde: restarDias(base, DIAS_GRACIA), ventana: `${ETIQUETA_PERIODO[frecuencia as Frecuencia]} de su programación más ${DIAS_GRACIA} días` };
}

// ── Evaluación de un ítem en una inspección ──
const EVALUABLES = new Set(['cumple', 'si_no', 'numero']);
const firmada = (i: InspAnexo1) => i.status === 'completada' && !!i.signed_at && !!i.performed_date;
export const preguntasDelItem = (i: Pick<InspAnexo1, 'template_snapshot'>, item: string): ItemFoto[] =>
  itemsDe(i.template_snapshot as FotoPlantilla).filter(it => it.anexo_item === item);

/** ¿La respuesta es una falla? En GT 22 los luxes se comparan con el mínimo para circulación (Iluminación). */
export function fallaDelItem(item: string, p: Pick<ItemFoto, 'response_type' | 'fail_value' | 'num_min' | 'num_max'>,
  r: Pick<RespAnexo1, 'value' | 'number_value'> | undefined): boolean {
  if (item === ITEM_LUX && p.response_type === 'numero') return !!r && r.number_value !== null && r.number_value !== undefined && r.number_value < MIN_LUX_CIRCULACION;
  return esFalla(p, r);
}

export interface EvaluacionItem {
  estado: EstadoCalculoAnexo1;
  respondidas: number;
  noAplica: number;
  fallas: number;
  resueltas: number;
  abiertas: number;
  vencidas: number;
  lux: number | null;
}

export function evaluarItem(item: string, i: InspAnexo1, respuestas: RespAnexo1[], hallazgos: HallAnexo1[], hoy: Date): EvaluacionItem {
  const ev: EvaluacionItem = { estado: 'pendiente', respondidas: 0, noAplica: 0, fallas: 0, resueltas: 0, abiertas: 0, vencidas: 0, lux: null };
  for (const p of preguntasDelItem(i, item).filter(x => EVALUABLES.has(x.response_type))) {
    const r = respuestas.find(x => x.inspection_id === i.id && x.item_key === p.id);
    const sinRespuesta = !r || ((r.value === null || r.value === undefined) && (r.number_value === null || r.number_value === undefined));
    if (sinRespuesta) continue;
    ev.respondidas++;
    if (item === ITEM_LUX && p.response_type === 'numero' && r!.number_value !== null && r!.number_value !== undefined) {
      ev.lux = ev.lux === null ? Number(r!.number_value) : Math.min(ev.lux, Number(r!.number_value));
    }
    if (r!.value === 'no_aplica') { ev.noAplica++; continue; }
    if (!fallaDelItem(item, p, r)) continue;
    ev.fallas++;
    const hs = hallazgos.filter(h => h.inspection_id === i.id && h.item_key === p.id);
    if (hs.length === 0) { ev.vencidas++; continue; }   // falla sin hallazgo: la medida no existe
    const estados = hs.map(h => estadoHallazgo(h, hoy));
    if (estados.every(e => e === 'cerrado')) ev.resueltas++;
    else if (estados.includes('vencido')) ev.vencidas++;
    else ev.abiertas++;
  }
  ev.estado = ev.respondidas === 0 ? 'pendiente'
    : ev.noAplica === ev.respondidas ? 'no_aplica'
      : ev.vencidas > 0 ? 'no_cumple'
        : ev.abiertas > 0 ? 'en_proceso'
          : 'cumple';
  return ev;
}

// ── Resultado por ítem ──
export interface ResultadoItemAnexo1 {
  estado: EstadoCalculoAnexo1;
  resumen: string;
  trazas: Traza[];
  /** Hay una inspección firmada vigente con preguntas del ítem (para GT 9: manda v2). */
  vigente: boolean;
  inspeccionId: string | null;
}

const traza = (estado: EstadoCalculoAnexo1, articulo: string, umbral: number | string | null, valor: number | string | null, unidad: string,
  nota: string, norma = NORMA_REGLA_ANEXO1): Traza => ({
  metodo: 'inspecciones_v2', norma, articulo_o_tabla: articulo, umbral, valor, unidad,
  estado: estado === 'cumple' ? 'cumple' : estado === 'no_cumple' ? 'no_cumple' : 'por_validar', fuente_doc: FUENTE_ANEXO1, nota,
});

export function resultadoItem(item: string, inspecciones: InspAnexo1[], respuestas: RespAnexo1[], hallazgos: HallAnexo1[],
  programaciones: ProgAnexo1[], hoy: Date = new Date()): ResultadoItemAnexo1 {
  const hoyIso = isoLocal(hoy);
  const L = legal(item);
  const candidatas = inspecciones.filter(i => firmada(i) && preguntasDelItem(i, item).length > 0)
    .sort((a, b) => ((b.performed_date as string).localeCompare(a.performed_date as string)) || ((b.signed_at as string).localeCompare(a.signed_at as string)));
  const conVigencia = candidatas.map(i => {
    const prog = programacionDe(i, programaciones, hoyIso);
    return { i, ...inicioVigencia(prog?.frequency ?? null, hoyIso) };
  });
  const vigente = conVigencia.find(x => (x.i.performed_date as string) >= x.desde);
  if (!vigente) {
    const ultima = conVigencia[0];
    const resumen = ultima
      ? `La última inspección firmada con preguntas de ${L} es del ${fechaEc(ultima.i.performed_date)} y ya no está vigente (${ultima.ventana}).`
      : `No hay inspecciones firmadas con preguntas de ${L}.`;
    return { estado: 'pendiente', resumen, vigente: false, inspeccionId: null,
      trazas: [traza('pendiente', `${L}: inspección firmada vigente`, 1, 0, 'inspecciones', resumen)] };
  }
  const ev = evaluarItem(item, vigente.i, respuestas, hallazgos, hoy);
  const fecha = fechaEc(vigente.i.performed_date);
  const base = `Inspección firmada del ${fecha} (vigencia: ${vigente.ventana}).`;
  const resumen = ev.estado === 'no_aplica' ? `${base} Todas las respuestas de ${L} están en "No aplica": el cálculo sugiere No aplica; lo confirma el administrador.`
    : ev.estado === 'pendiente' ? `${base} Las preguntas de ${L} no tienen respuestas que se puedan evaluar.`
      : ev.estado === 'no_cumple' ? `${base} ${plural(ev.vencidas, 'falla de', 'fallas de')} ${L} con el hallazgo vencido o sin hallazgo.`
        : ev.estado === 'en_proceso' ? `${base} ${plural(ev.abiertas, 'falla de', 'fallas de')} ${L} con el hallazgo abierto dentro de su plazo.`
          : ev.resueltas > 0 ? `${base} Las fallas de ${L} tienen su hallazgo cerrado con evidencia.`
            : `${base} Todas las respuestas de ${L} cumplen.`;
  const trazas = [traza(ev.estado, `${L}: respuestas de la última inspección firmada vigente`, ev.respondidas, ev.respondidas - ev.fallas + ev.resueltas,
    'respuestas que cumplen', resumen)];
  if (item === ITEM_LUX && ev.lux !== null) {
    const ok = ev.lux >= MIN_LUX_CIRCULACION;
    // El Checklist muestra "norma · artículo — estado": el valor medido y el mínimo van en el artículo.
    trazas.push(traza(ok ? 'cumple' : 'no_cumple',
      `Art. 10.4: áreas de circulación y pasillos, medido ${lux(ev.lux)} lux (mínimo ${lux(MIN_LUX_CIRCULACION)} lux)`, MIN_LUX_CIRCULACION, ev.lux, 'lux',
      ok ? 'La iluminación medida alcanza el mínimo.' : 'La iluminación medida está por debajo del mínimo.', NORMA_LUX));
  }
  return { estado: ev.estado, resumen, trazas, vigente: true, inspeccionId: vigente.i.id };
}

// ── Escritura y presentación (mismo patrón que Incidentes, lote E) ──
export interface TrazaItemAnexo1 extends Omit<TrazaItemPlan, 'estado'> {
  estado: EstadoCalculoAnexo1;
  etiquetaGuardado?: string;
  notaGuardado?: string;
}

/** ¿Qué escribe el sync? null = nada. Nunca lo manual, nunca sobre "No aplica" y nunca "No aplica". */
export function decisionAnexo1(existente: EstadoGuardadoChecklist | undefined, deseado: EstadoCalculoAnexo1): EstadoItem | null {
  if (existente?.estado === 'no_aplica') return null;
  if (esManual(existente)) return null;
  if (deseado === 'no_aplica') return null;
  if (existente?.estado === deseado && existente.auto_source === FUENTE_INSPECCIONES_ANEXO1) return null;
  if (!existente && deseado === 'pendiente') return null;
  return deseado;
}

const ETIQUETA_GUARDADO: Record<string, string> = { cumple: 'Cumple', en_proceso: 'En proceso', no_cumple: 'No cumple', pendiente: 'Pendiente', no_aplica: 'No aplica' };

export function presentacionAnexo1(existente: EstadoGuardadoChecklist | undefined, r: Pick<ResultadoItemAnexo1, 'estado' | 'resumen' | 'trazas'>): TrazaItemAnexo1 {
  const base: TrazaItemAnexo1 = { estado: r.estado, resumen: r.resumen, trazas: r.trazas, manualRespetado: false, estadoManual: null,
    modulo: MODULO_INSPECCIONES_ANEXO1, fuente: FUENTE_INSPECCIONES_ANEXO1 };
  const guardado = existente?.estado ?? 'pendiente';
  if (existente?.estado === 'no_aplica') {
    if (r.estado === 'no_aplica') return base;
    return { ...base, manualRespetado: true, estadoManual: 'no_aplica', etiquetaGuardado: 'Marcado como No aplica',
      notaGuardado: 'Un "No aplica" guardado no cambia solo: lo cambia el administrador.' };
  }
  if (esManual(existente)) {
    if (existente!.estado === r.estado) return base;
    return { ...base, manualRespetado: true, estadoManual: existente!.estado };
  }
  if (r.estado === 'no_aplica') {
    return { ...base, manualRespetado: true, estadoManual: guardado, etiquetaGuardado: `Estado guardado: ${ETIQUETA_GUARDADO[guardado] ?? guardado}`,
      notaGuardado: 'El cálculo no marca "No aplica" solo: lo confirma el administrador.' };
  }
  return base;
}

/** GT 9 sin inspección v2 vigente: manda el módulo anterior; la traza lo dice. */
export function trazaGT9ModuloAnterior(estado: EstadoItem): TrazaItemAnexo1 {
  const resumen = 'No hay una inspección del motor nuevo firmada y vigente con la pregunta de GT 9: el estado lo calcula el módulo anterior de Inspecciones (una inspección finalizada con todas sus preguntas respondidas).';
  return { estado, resumen, manualRespetado: false, estadoManual: null, modulo: MODULO_INSPECCIONES_ANTERIOR, fuente: 'inspecciones',
    trazas: [traza(estado, 'GT 9: verificación in situ de las medidas', 1, estado === 'cumple' ? 1 : 0, 'inspecciones finalizadas completas', resumen)] };
}

export interface SyncAnexo1 { trazas: Record<string, TrazaItemAnexo1>; v2MandaGT9: boolean }

export function resultadosAnexo1(inspecciones: InspAnexo1[], respuestas: RespAnexo1[], hallazgos: HallAnexo1[], programaciones: ProgAnexo1[],
  hoy: Date = new Date()): Record<string, ResultadoItemAnexo1> {
  return Object.fromEntries(ITEMS_ANEXO1_INSPECCIONES.map(item => [item, resultadoItem(item, inspecciones, respuestas, hallazgos, programaciones, hoy)]));
}

// omitirGT9: los informes por puesto mandan en GT 9 (hay un informe de medidas aprobado): GT-14 no se escribe ni se muestra aquí.
export async function syncInspeccionesAnexo1(companyId: string, hoy: Date = new Date(), opciones: { omitirGT9?: boolean } = {}): Promise<SyncAnexo1 | null> {
  if (!companyId) return null;
  const [{ data: insps, error: e1 }, { data: resps, error: e2 }, { data: halls, error: e3 }, { data: progs, error: e4 }, { data: existentes, error: e5 }] = await Promise.all([
    supabase.from('insp_inspections').select('id, status, signed_at, performed_date, schedule_id, template_id, template_snapshot').eq('company_id', companyId),
    supabase.from('insp_answers').select('inspection_id, item_key, value, number_value').eq('company_id', companyId),
    supabase.from('insp_findings').select('inspection_id, item_key, status, due_date').eq('company_id', companyId),
    supabase.from('insp_schedules').select('id, template_id, frequency, active, end_date').eq('company_id', companyId),
    supabase.from('checklist_item_estados').select('item_id, estado, auto_source').eq('company_id', companyId).in('item_id', ITEMS_ANEXO1_INSPECCIONES),
  ]);
  // Sin M41 en la base (o sin permiso) no se toca nada; sin inspecciones del motor nuevo, tampoco.
  if (e1 || e2 || e3 || e4) return null;
  const i = (insps ?? []) as InspAnexo1[];
  if (i.length === 0) return { trazas: {}, v2MandaGT9: false };
  const res = resultadosAnexo1(i, (resps ?? []) as RespAnexo1[], (halls ?? []) as HallAnexo1[], (progs ?? []) as ProgAnexo1[], hoy);
  const v2MandaGT9 = res[ITEM_GT9].vigente;
  const items = ITEMS_ANEXO1_INSPECCIONES.filter(item => item !== ITEM_GT9 || (v2MandaGT9 && !opciones.omitirGT9));
  const porItem = new Map(((existentes ?? []) as EstadoGuardadoChecklist[]).map(e => [e.item_id, e]));
  const ahora = new Date().toISOString();
  const filas = items.flatMap(item => {
    const d = decisionAnexo1(porItem.get(item), res[item].estado);
    return d ? [{ company_id: companyId, item_id: item, estado: d, auto_source: FUENTE_INSPECCIONES_ANEXO1, updated_at: ahora }] : [];
  });
  if (filas.length && !e5) {
    const { error } = await supabase.from('checklist_item_estados').upsert(filas, { onConflict: 'company_id,item_id' });
    if (error) console.error('syncInspeccionesAnexo1', error);
  }
  // Traza visible si hay inspección vigente, si el estado ya lo escribió este cálculo o si es un ítem nuevo (dice cómo
  // se cumple). Un SP o GT 9 sin inspección vigente no muestra nada: su estado manual o del módulo anterior sigue igual.
  const visible = (item: string) => res[item].vigente || porItem.get(item)?.auto_source === FUENTE_INSPECCIONES_ANEXO1
    || (ITEMS_GT_NUEVOS as readonly string[]).includes(item) || (ITEMS_GT_MANTENIMIENTO as readonly string[]).includes(item)
    || (ITEMS_GT_CONDICIONES_HIGIENE as readonly string[]).includes(item);
  const trazas = Object.fromEntries(items.filter(visible).map(item => {
    const d = decisionAnexo1(porItem.get(item), res[item].estado);
    const existente = d && !e5 ? { item_id: item, estado: d, auto_source: FUENTE_INSPECCIONES_ANEXO1 } : porItem.get(item);
    return [item, presentacionAnexo1(existente, res[item])];
  }));
  return { trazas, v2MandaGT9 };
}

export const justificacionNoAplicaAnexo1 = (resumen: string) => `Cálculo de Inspecciones: ${resumen}`;

/**
 * "Usar cálculo automático": quita lo manual y escribe el cálculo con la fuente de Inspecciones. "No aplica" se escribe
 * con la justificación del cálculo (solo el administrador, lo exige la base). Para salir de un "No aplica" guardado, la
 * base pide que el cambio no venga de un sync: primero se guarda el estado sin fuente y después se le pone la fuente.
 */
export async function aplicarCalculoAnexo1(companyId: string, itemId: string, estado: EstadoCalculoAnexo1, resumen: string): Promise<void> {
  const { data, error } = await supabase.from('checklist_item_estados').select('estado').eq('company_id', companyId).eq('item_id', itemId).maybeSingle();
  if (error) throw new Error(`No se pudo leer el estado de ${legal(itemId)}: ${error.message}`);
  const actual = (data as { estado: string } | null)?.estado;
  const ahora = new Date().toISOString();
  const mensaje = (e: { code?: string; message: string }) => (e.code === '42501'
    ? (estado === 'no_aplica' || actual === 'no_aplica' ? 'Solo el administrador de la empresa puede marcar o quitar "No aplica".' : 'No tienes permiso para cambiar el Checklist.')
    : `No se pudo aplicar el cálculo en ${legal(itemId)}: ${e.message}`);
  if (estado === 'no_aplica') {
    const { data: u } = await supabase.auth.getUser();
    const { error: e } = await supabase.from('checklist_item_estados').upsert({
      company_id: companyId, item_id: itemId, estado: 'no_aplica', auto_source: FUENTE_INSPECCIONES_ANEXO1, updated_at: ahora,
      no_aplica_justificacion: justificacionNoAplicaAnexo1(resumen), no_aplica_por: u?.user?.id ?? null, no_aplica_at: ahora,
    }, { onConflict: 'company_id,item_id' });
    if (e) throw new Error(mensaje(e));
    return;
  }
  if (actual === 'no_aplica') {
    const { error: e } = await supabase.from('checklist_item_estados').update({ estado, auto_source: null, updated_at: ahora })
      .eq('company_id', companyId).eq('item_id', itemId);
    if (e) throw new Error(mensaje(e));
  }
  const { error: e2 } = await supabase.from('checklist_item_estados').upsert(
    { company_id: companyId, item_id: itemId, estado, auto_source: FUENTE_INSPECCIONES_ANEXO1, updated_at: ahora },
    { onConflict: 'company_id,item_id' });
  if (e2) throw new Error(mensaje(e2));
}

// ── Aplicabilidad de GT 14 (GT-20): la empresa registra productos químicos o bodegas (la regla vive en el motor) ──
/** true/false según los conteos; null si no se pudo leer (el ítem aplica: no se marca No aplica sin saber). */
export async function tieneQuimicosOBodegas(companyId: string): Promise<boolean | null> {
  if (!companyId) return null;
  const [{ count: productos, error: e1 }, { count: bodegas, error: e2 }] = await Promise.all([
    supabase.from('products').select('id', { head: true, count: 'exact' }).eq('company_id', companyId).is('deleted_at', null),
    supabase.from('warehouses').select('id', { head: true, count: 'exact' }).eq('company_id', companyId),
  ]);
  if (e1 || e2) return null;
  return (productos ?? 0) > 0 || (bodegas ?? 0) > 0;
}
